import { useMemo } from 'react';
import { dwrClassesById } from '@/data/classes';
import { findMoveByIdGlobal } from '@/data/moves';
import type { Character, MoveRef } from '@/schemas/character';
import type { ClassId } from '@/schemas/classId';
import type { Move } from '@/schemas/move';
import { getBundleMovesToApply } from '@/services/bundle';
import { MoveSetupPanel } from '../../sections/MoveSetupPanel';
import { updateMoveChoiceGroup, updateMoveNarrativeField } from '../../services/moveRefMutations';

/**
 * **D7-bundle (FI-17 follow-up)**: sezione del setup per le mosse del
 * bundle di una mossa anchor (es. *Lanciare Incantesimi Divini* →
 * bundle `[chierico.divinita, chierico.mana]`).
 *
 * Estratto in file separato da `LevelUpStepMulticlass.tsx` per rispettare
 * `max-lines: 400` dopo l'estensione D7-bundle.
 *
 * Mostra:
 *  - Lista informativa di TUTTE le mosse del bundle (con nome + classe).
 *  - Per ogni mossa del bundle con `hasMoveSetup`, un `MoveSetupEditor`
 *    annidato (pattern coerente con `NestedMoveSetup` della mossa-target
 *    multiclasse).
 *
 * Le mosse del bundle senza setup interattivo sono solo voci informative.
 */
export interface BundleSetupsSectionProps {
  character: Character;
  /** Mossa anchor; può essere `undefined` o priva di `bundles` — la sezione
   *  in quel caso non renderizza nulla. */
  anchorMove: Move | undefined;
  bundleMovesNeedingSetup: readonly Move[];
  bundleSetups: Readonly<Record<string, Omit<MoveRef, 'id'>>>;
  onBundleSetupsChange: (next: Record<string, Omit<MoveRef, 'id'>>) => void;
}

export function BundleSetupsSection(props: BundleSetupsSectionProps) {
  const { character, anchorMove, bundleMovesNeedingSetup, bundleSetups, onBundleSetupsChange } =
    props;
  // Lista completa del bundle (per voce informativa), filtrata via
  // getBundleMovesToApply per idempotenza/dedup Mana. Se `anchorMove`
  // non ha `bundles`, la lista è vuota e il componente non renderizza.
  const allBundleMoves = useMemo<readonly Move[]>(
    () => (anchorMove?.bundles ? getBundleMovesToApply(anchorMove, character) : []),
    [anchorMove, character],
  );
  if (allBundleMoves.length === 0) return null;
  return (
    <div className="border-rule bg-paper-soft/40 border p-3">
      <p className="font-display text-ink-soft mb-2 text-[0.78rem] tracking-[0.08em] uppercase">
        Otterrai anche {allBundleMoves.length === 1 ? 'la mossa' : 'le mosse'}:
      </p>
      <ul className="mb-3 list-disc space-y-1 pl-5 font-serif text-[0.92rem]">
        {allBundleMoves.map((m) => (
          <li key={m.id}>
            <span className="font-semibold">{m.name}</span>
            <span className="text-ink-soft ml-2 text-[0.78rem] italic">
              ({classLabel(getMoveClassId(m))})
            </span>
          </li>
        ))}
      </ul>
      {bundleMovesNeedingSetup.map((bundleMove) => {
        const setup = bundleSetups[bundleMove.id];
        const moveClassId = getMoveClassId(bundleMove);
        return (
          <BundleMoveSetupEditor
            key={bundleMove.id}
            character={character}
            sourceClassId={moveClassId}
            move={bundleMove}
            selectedMoveSetup={setup}
            onChange={(newSetup) => {
              onBundleSetupsChange({ ...bundleSetups, [bundleMove.id]: newSetup });
            }}
          />
        );
      })}
    </div>
  );
}

interface BundleMoveSetupEditorProps {
  character: Character;
  sourceClassId: ClassId;
  move: Move;
  selectedMoveSetup: Omit<MoveRef, 'id'> | undefined;
  onChange: (setup: Omit<MoveRef, 'id'>) => void;
}

function BundleMoveSetupEditor(props: BundleMoveSetupEditorProps) {
  const { character, sourceClassId: _sourceClassId, move, selectedMoveSetup, onChange } = props;
  const moveRef: MoveRef = {
    id: move.id,
    selectedChoices: selectedMoveSetup?.selectedChoices ?? [],
    ...(selectedMoveSetup?.selectedChoiceGroups && {
      selectedChoiceGroups: selectedMoveSetup.selectedChoiceGroups,
    }),
    ...(selectedMoveSetup?.narrativeFieldValues && {
      narrativeFieldValues: selectedMoveSetup.narrativeFieldValues,
    }),
  };
  // FI-79: ownedMoveIds del PG per disabilitare spell-options con
  // `requiresMove` non soddisfatto. Coerente con builder L1 + altri caller LevelUp.
  const ownedMoveIds = useMemo(() => character.moves.map((m) => m.id), [character.moves]);
  return (
    <div className="border-rule bg-paper-bright mt-3 border p-3">
      <p className="font-display text-ink-soft mb-2 text-[0.78rem] tracking-[0.08em] uppercase">
        Setup di «{move.name}»
      </p>
      <MoveSetupPanel
        move={move}
        selectedByGroup={buildSelectedByGroup(move, moveRef)}
        narrativeValues={moveRef.narrativeFieldValues ?? {}}
        recruitOutcome={moveRef.recruitOutcome}
        archetypeId={character.archetypeId}
        ownedMoveIds={ownedMoveIds}
        onChoiceGroupChange={(moveId, groupId, selectedIds) => {
          const updated = updateMoveChoiceGroup(moveRef, moveId, groupId, selectedIds);
          onChange(extractSetup(updated));
        }}
        onNarrativeFieldChange={(moveId, fieldId, value) => {
          const updated = updateMoveNarrativeField(moveRef, moveId, fieldId, value);
          onChange(extractSetup(updated));
        }}
      />
    </div>
  );
}

/**
 * Proietta un `MoveRef` in `selectedByGroup` per `MoveSetupPanel`.
 * Coerente con `LevelUpStepMove.buildSelectedByGroup` /
 * `LevelUpStepMulticlass.buildSelectedByGroup`.
 */
function buildSelectedByGroup(move: Move, moveRef: MoveRef): Record<string, string[]> {
  const result: Record<string, string[]> = {};
  if (move.choices) result.main = [...moveRef.selectedChoices];
  if (moveRef.selectedChoiceGroups) {
    for (const [groupId, ids] of Object.entries(moveRef.selectedChoiceGroups)) {
      result[groupId] = [...ids];
    }
  }
  return result;
}

function getMoveClassId(move: Move): ClassId {
  const lookup = findMoveByIdGlobal(move.id);
  const id = lookup?.classId ?? move.classId;
  if (id === undefined) {
    // Le mosse del bundle hanno sempre classId nel catalogo DWR. Caso
    // patologico: fallback su 'guerriero' (mai usato in pratica).
    return 'guerriero';
  }
  return id as ClassId;
}

function classLabel(classId: ClassId): string {
  const def = dwrClassesById[classId];
  return def?.name ?? classId;
}

function extractSetup(moveRef: MoveRef): Omit<MoveRef, 'id'> {
  const setup: Omit<MoveRef, 'id'> = { selectedChoices: moveRef.selectedChoices };
  if (moveRef.selectedChoiceGroups) setup.selectedChoiceGroups = moveRef.selectedChoiceGroups;
  if (moveRef.narrativeFieldValues) setup.narrativeFieldValues = moveRef.narrativeFieldValues;
  return setup;
}
